/**
 * Runtime feature flags.
 *
 * Deliberately a standalone module rather than part of `app/constants.ts`:
 * that module pulls in `@mitratech/north-star` (icons, nav panel specs), and
 * these flags are read from low-level `lib/` utilities where dragging in the
 * component library would be both heavy and a circular-import hazard.
 *
 * Precedence differs deliberately from `app/constants.ts`'s
 * `CREATE_CLIENT_ROUTE_SLUG`. That one uses `||`, so an empty runtime value
 * falls through to the build-time default — right for a slug, wrong for a
 * privacy opt-in. Here the container-injected value is authoritative whenever
 * the **key is present at all**, empty string included: the entrypoint script
 * (`docker/entrypoint.d/50-inject-runtime-env.sh`) always writes the key, as
 * `""` when the task definition leaves it unset, so key-presence is a reliable
 * "this deployment has spoken" signal.
 *
 * Falling through on `""` would mean a `VITE_ARIES_CHAT_TRANSCRIPT=true` baked
 * into an image could never be switched off by a task definition — the flag
 * would be impossible to disable for the tenants that require it disabled,
 * which is the entire reason it exists.
 *
 * Local dev has no injected key (`public/env-config.js` sets
 * `window.__RUNTIME_ENV__ = {}`), so it still falls back to the build-time
 * `import.meta.env` value from `.env.local`.
 *
 * @module config/featureFlags
 */

/**
 * Whether ARIES chat transcripts may be persisted to browser storage so an
 * abandoned Case can be resumed with its prior conversation visible.
 *
 * **Opt-in (default off).** Resuming a Case does not depend on this: the
 * backend `continue-chat` trigger re-asks the outstanding question either
 * way (see `CaseProvider`). With the flag off the Admin simply doesn't see
 * the earlier bubbles — which is the required behavior for deployments that
 * must not keep conversation content on the device.
 *
 * Read lazily (a function, not a module-scope constant) so the value can be
 * toggled per test and so a deployment flip doesn't depend on import order.
 */
export function isChatTranscriptEnabled(): boolean {
  const runtime = window.__RUNTIME_ENV__;
  const value =
    runtime && "VITE_ARIES_CHAT_TRANSCRIPT" in runtime
      ? runtime.VITE_ARIES_CHAT_TRANSCRIPT
      : import.meta.env.VITE_ARIES_CHAT_TRANSCRIPT;

  return value === "true";
}

/**
 * Whether the embedded Amazon Quick assessment (`/assessment`) is offered in
 * the navigation (LOA-1426).
 *
 * **Opt-in (default off)**, with the same precedence as the flag above: an
 * injected runtime key wins even when empty, so a deployment can switch it
 * off. This only controls visibility. The backend's `QUICK_ASSESSMENT_ENABLED`
 * is the source of truth and answers 404 when off.
 */
export function isQuickAssessmentEnabled(): boolean {
  const runtime = window.__RUNTIME_ENV__;
  const value =
    runtime && "VITE_QUICK_ASSESSMENT_ENABLED" in runtime
      ? runtime.VITE_QUICK_ASSESSMENT_ENABLED
      : import.meta.env.VITE_QUICK_ASSESSMENT_ENABLED;

  return value === "true";
}

/**
 * Whether the `/intake` chat uses the AgentCore agent instead of the scripted
 * dialogue (LOA-1491).
 *
 * **Opt-in (default off: scripted)**, with the same precedence as the flags
 * above: an injected runtime key wins even when empty, so a task definition can
 * choose the agent for one environment without rebuilding the image. This only
 * chooses which agent the page asks. The backend's `INTAKE_AGENT_HARNESS_ARN`
 * decides whether the agent route answers at all (503 when unset).
 */
export function isIntakeAgentCore(): boolean {
  const runtime = window.__RUNTIME_ENV__;
  const value =
    runtime && "VITE_INTAKE_AGENT" in runtime ? runtime.VITE_INTAKE_AGENT : import.meta.env.VITE_INTAKE_AGENT;

  return value === "agentcore";
}
